import { useState, useEffect, Fragment, useMemo } from 'react'
import last from 'lodash/last'
import isNil from 'lodash/isNil'
import findIndex from 'lodash/findIndex'
import { useTranslation } from 'react-i18next'
import uniqBy from 'lodash/uniqBy'
import { useParams } from 'react-router'
import dayjs from 'dayjs'
import { createPortal } from 'react-dom'

import { Post } from 'lib/_types'

import {
  useGroupOrThreadOrPostPageData,
  useInfinityScrollSimple,
  usePostActions,
} from 'hooks'

import { useGetConversationQuery } from 'store/api/yay/conversation'

import SeoHeader from 'components/SEOHeader'
import BoxHeader from 'components/BoxHeader'
import PostItemSkeleton from 'components/Post/PostItem/PostItemSkeleton'
import PostItem from 'components/Post/PostItem'
import LoadMore from 'components/LoadMore'

import { st } from '../lib/helpers'
import { redirectToPreviousPage } from 'lib/router'

const Conversations = () => {
  const { conversationId } = useParams()
  const { t } = useTranslation()
  const { groupId, groupRes, threadId } = useGroupOrThreadOrPostPageData(
    ({ thread }) => [
      {
        title: thread?.id
          ? t('group_thread_conversation_page.app_common_conversation_thread')
          : t('conversations_page.breadcrumb.group_detail'),
      },
    ]
  )
  const isThreadPage = !isNil(threadId)
  const [__domReady, __domReadySet] = useState(false)

  const [__fromId, __fromIdSet] = useState<string>('0')
  const [__posts, __postsSet] = useState<Array<Post>>([])

  const postActions = usePostActions({
    groupId,
    postList: __posts,
    setter: __postsSet,
    onReplyCallback: (createdPostData) => {
      __postsSet((prevState) => {
        return prevState?.map?.((x) => {
          if (x?.id == createdPostData?.parentPost?.id) {
            return {
              ...x,
              in_reply_to_post_count: x?.in_reply_to_post_count + 1,
            }
          }

          return x
        })
      })

      // Conversation's reply logic is a bit different from `appendReply`.
      // Because the order is from old to new.
      window.setTimeout(() => {
        conversationRes?.refetch?.()
      }, 1000)
    },
  })

  const conversationRes = useGetConversationQuery({
    group_id: groupId,
    thread_id: threadId,
    conversationId,
    reverse: true,
    from_post_id: __fromId,
  })

  const loadMore = () => {
    __fromIdSet(last(conversationRes?.data?.posts)?.id?.toString?.())
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    __postsSet(
      uniqBy(
        [
          ...__posts?.map?.((x) => {
            return (
              conversationRes?.data?.posts?.find?.((y) => y?.id == x?.id) ?? x
            )
          }),
          ...(conversationRes?.data?.posts ?? []),
        ],
        (x) => x?.id
      )
    )
  }, [conversationRes])

  useEffect(() => {
    __fromIdSet('0')
    __postsSet([])
  }, [conversationId])

  const _rootPost = __posts?.find?.(
    (x) => !x?.hasOwnProperty?.('in_reply_to_post')
  )

  // This is a heavy calculation (loops and recursion), so better memoise it.
  const nonRootPostsSorted = useMemo(() => {
    const _descendants = __posts?.filter?.((x) =>
      x?.hasOwnProperty?.('in_reply_to_post')
    )

    let _sortedDescendants: Array<{
      post: Post
      isReply: boolean
    }> = []

    _descendants?.forEach?.((descendant) => {
      const findComment = (currentPost: Post): Post => {
        const parentPost = _sortedDescendants?.find?.(
          (x) => x?.post?.id == currentPost?.in_reply_to_post?.id
        )

        // `parentPost` being null means either
        // the parent is the current root post, or the parent has been deleted.
        // In the latter case, this `currentPost` becomes an orphan,
        // which will be treated as a comment.
        if (isNil(parentPost)) {
          return null
        }

        // Stop at the comment level.
        if (parentPost?.isReply == false) {
          return parentPost?.post
        }

        return findComment(parentPost?.post)
      }

      // Find the comment for each reply, no matter how deep it is.
      const commentPost = findComment(descendant)

      if (!isNil(commentPost)) {
        const findFamilyLength = (currentPost: Post) => {
          const currentPostChildren = _sortedDescendants?.filter?.(
            (y) => y?.post?.in_reply_to_post?.id == currentPost?.id
          )

          if (currentPostChildren?.length == 0) {
            return 0
          }

          let currentPostFamilyLength = currentPostChildren?.length

          // As long as there are children, we keep going deeper.
          currentPostChildren?.forEach?.((currentPostChild) => {
            currentPostFamilyLength =
              currentPostFamilyLength + findFamilyLength(currentPostChild?.post)
          })

          return currentPostFamilyLength
        }

        // We then proceed to find the parent index, then we'll find
        // how many posts in the parent's family (i.e. whole children of children of children, etc.),
        // and together those number shall be the position of current post.
        const toBeAppendedIndex =
          findIndex(_sortedDescendants, (y) => y?.post?.id == commentPost?.id) +
          findFamilyLength(commentPost)

        _sortedDescendants = [
          ..._sortedDescendants?.slice?.(0, toBeAppendedIndex + 1),
          {
            post: descendant,
            isReply: true,
          },
          ..._sortedDescendants?.slice?.(
            toBeAppendedIndex + 1,
            _sortedDescendants?.length
          ),
        ]
      } else {
        // Comments and orphans will be added to the end.
        _sortedDescendants = [
          ..._sortedDescendants,
          {
            post: descendant,
            isReply: false, // And be treated as a comment level.
          },
        ]
      }
    })

    return _sortedDescendants
  }, [__posts])

  // prevent react portal error when target not found
  useEffect(() => {
    __domReadySet(true)
  }, [])

  if (!__domReady && isThreadPage) return null
  return (
    <Fragment>
      {conversationRes?.isSuccess &&
        (isNil(groupId) ? true : groupRes?.isSuccess) && (
          <SeoHeader
            title={st(
              groupId ? 'conversation_group.title' : 'conversation.title',
              {
                post_content: _rootPost?.text,
                username: _rootPost?.user?.nickname,
              }
            )}
            description={st(
              groupId
                ? 'conversation_group.description'
                : 'conversation.description',
              {
                post_content: _rootPost?.text,
                username: _rootPost?.user?.nickname,
                circle_name: groupRes?.data?.topic,
                post_date: dayjs(_rootPost?.created_at * 1000).format(
                  'YYYY-MM-DD HH:mm:ss'
                ),
              }
            )}
            image={_rootPost?.user?.profile_icon}
          />
        )}

      {(() => {
        const root = (() => {
          if (conversationRes?.isFetching && __fromId == '0') {
            return <PostItemSkeleton />
          }

          if (isNil(_rootPost)) {
            return (
              <div className="Conversations__root-post-deleted">
                {t('reply_to.post_reply_target_null')}
              </div>
            )
          }

          return (
            <div className="PostList">
              <PostItem
                key={_rootPost?.id}
                post={_rootPost}
                index={0}
                isLast
                showReposts
                {...postActions}
              />
            </div>
          )
        })()

        return isThreadPage ? (
          createPortal(root, document.querySelector('.GroupThread'))
        ) : (
          <div className="Box Box--mobile-mb">
            <BoxHeader
              nav={[
                {
                  label: t('common.back'),
                  isBack: true,
                  onClick: () => {
                    sessionStorage.setItem('timeline-back-navigation', 'true')
                    redirectToPreviousPage()
                  },
                },
              ]}
            />
            {root}
          </div>
        )
      })()}

      {(() => {
        if (nonRootPostsSorted?.length == 0) {
          return null
        }

        return (
          <div className="Box">
            <div className="PostList">
              {nonRootPostsSorted?.map?.((x, i, _self) => (
                <PostItem
                  key={x?.post?.id}
                  post={x?.post}
                  index={i + 1} // root post is index 0
                  group={groupRes}
                  isConversationLayout={_self?.[i + 1]?.isReply}
                  isConversationLayoutSmall={x?.isReply}
                  isLast={i == nonRootPostsSorted?.length - 1}
                  showReposts
                  {...postActions}
                />
              ))}

              <LoadMore
                isLoading={conversationRes?.isFetching}
                hidden={conversationRes?.currentData?.posts?.length == 0}
                className="GroupThreads__load-more"
                onClick={() => {
                  loadMore()
                }}
                ref={loadMoreRef}
              />
            </div>
          </div>
        )
      })()}
    </Fragment>
  )
}

export default Conversations
